import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, UserCog, User, Eye, EyeOff } from "lucide-react";
import { api, ROLES } from "@/api";
import { useAuth } from "@/auth";
import { C } from "@/theme";
import { Button } from "@/ui";

function roleIcon(role, color, size = 22) {
  if (role === "Admin") return <ShieldCheck size={size} color={color} />;
  if (role === "Rider") return <User size={size} color={color} />;
  return <UserCog size={size} color={color} />;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const [fpSent, setFpSent] = useState(false);

  const usersQ = useQuery({
    queryKey: ["auth-users", role],
    queryFn: () => api.get(`/auth/users?role=${encodeURIComponent(role)}`),
    enabled: !!role,
  });
  const options = usersQ.data ?? [];

  const pickRole = (r) => { setRole(r); setSelected(null); setPassword(""); setError(""); };

  const onSubmit = async () => {
    if (!role || !selected) return;
    setError(""); setBusy(true);
    try {
      const user = await api.post("/auth/login", { role, user_id: selected, password });
      login(user);
      navigate("/");
    } catch (e) {
      setError(e?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onForgot = async () => {
    setFpBusy(true);
    try {
      await api.post("/auth/request-password-reset", { email: fpEmail.trim(), origin: window.location.origin });
    } catch { /* never reveal failures */ }
    setFpBusy(false);
    setFpSent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: C.surface }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-6">
          <img src="/rospa-logo.png" alt="RoSPA Advanced Riders West Midlands" className="w-56 object-contain" data-testid="login-logo" />
          <p className="text-white text-xl font-extrabold font-display tracking-tight" data-testid="login-app-name">WMR Training App</p>
        </div>

        <div style={{ background: C.surfaceSecondary, borderColor: C.border }} className="rounded-3xl border p-6">
          <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">Select your role</label>
          <div className="grid grid-cols-2 gap-2.5 mt-2.5">
            {ROLES.map((r) => {
              const active = role === r;
              return (
                <button key={r} data-testid={`role-${r}`} onClick={() => pickRole(r)}
                  style={{ background: active ? C.brandPrimary : C.surfaceTertiary, borderColor: active ? C.brandPrimary : C.border }}
                  className="flex items-center gap-2.5 rounded-2xl border px-3.5 py-4 transition-colors">
                  {roleIcon(r, active ? "#fff" : C.brandPrimary)}
                  <span style={{ color: active ? "#fff" : C.onSurface }} className="font-bold text-sm">{r}</span>
                </button>
              );
            })}
          </div>

          {role ? (
            <div className="mt-5">
              <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">Select user</label>
              <select data-testid="user-dropdown" value={selected ?? ""} onChange={(e) => { setSelected(e.target.value); setError(""); }}
                style={{ background: C.surfaceTertiary, borderColor: C.border, color: C.onSurface }}
                className="w-full mt-2 rounded-xl border px-3.5 h-12 text-[15px] font-semibold outline-none">
                <option value="">{usersQ.isLoading ? "Loading…" : options.length ? "Select user" : "No accounts in this role"}</option>
                {options.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>

              {selected ? (
                <div className="mt-4">
                  <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">Password</label>
                  <div style={{ background: C.surfaceTertiary, borderColor: C.border }} className="flex items-center rounded-xl border px-3.5 mt-2">
                    <input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && onSubmit()} placeholder="Enter password" style={{ color: C.onSurface }} className="flex-1 bg-transparent h-12 text-[15px] outline-none" />
                    <button data-testid="toggle-password" onClick={() => setShowPw((v) => !v)}>{showPw ? <EyeOff size={20} color={C.muted} /> : <Eye size={20} color={C.muted} />}</button>
                  </div>
                </div>
              ) : null}

              {error ? <p data-testid="login-error" style={{ color: C.error }} className="text-[13px] font-semibold mt-3">{error}</p> : null}
              <div className="mt-5"><Button label="Sign In" onClick={onSubmit} testID="login-continue" disabled={!selected || !password} loading={busy} /></div>
            </div>
          ) : null}
        </div>

        <div className="mt-4 text-center">
          {!fpOpen ? (
            <button data-testid="forgot-password-link" onClick={() => { setFpOpen(true); setFpSent(false); }} style={{ color: C.mutedInverse }} className="text-[13px] font-semibold underline">Forgot password?</button>
          ) : (
            <div style={{ background: C.surfaceSecondary, borderColor: C.border }} className="rounded-2xl border p-4 text-left">
              <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">Reset your password</label>
              {fpSent ? (
                <p data-testid="forgot-password-sent" style={{ color: C.onSurface }} className="text-[13px] mt-2">If that email is on your account, we've sent a reset link. Check your inbox (and spam).</p>
              ) : (
                <>
                  <p style={{ color: C.muted }} className="text-[12px] mt-1">Enter the email on your account and we'll send a reset link.</p>
                  <input data-testid="forgot-password-email" type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="you@example.com" style={{ background: C.surfaceTertiary, borderColor: C.border, color: C.onSurface }} className="w-full mt-2 rounded-xl border px-3.5 h-11 text-[15px] outline-none" />
                  <div className="mt-3"><Button label="Send reset link" testID="forgot-password-submit" onClick={onForgot} disabled={!fpEmail.trim()} loading={fpBusy} /></div>
                </>
              )}
              <button data-testid="forgot-password-cancel" onClick={() => { setFpOpen(false); setFpEmail(""); setFpSent(false); }} style={{ color: C.mutedInverse }} className="text-[12px] font-semibold mt-3 underline">Back to sign in</button>
            </div>
          )}
        </div>
        <p style={{ color: C.mutedInverse }} className="text-center text-[13px] mt-6">Choose your role, pick your name, then enter your password</p>
      </div>
    </div>
  );
}
